import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, UserCheck, UserX, Clock } from "lucide-react";
import { API } from "@/lib/api";


export default function ApprovalsPage() {
  const { authHeaders } = useAuth();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState({});

  const load = () => {
    axios.get(`${API}/auth/pending`, { headers: authHeaders() })
      .then((r) => setPending(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleApprove = async (userId) => {
    setActing((a) => ({ ...a, [userId]: "approve" }));
    try {
      await axios.post(`${API}/auth/approve/${userId}`, {}, { headers: authHeaders() });
      load();
    } finally {
      setActing((a) => ({ ...a, [userId]: null }));
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm("Reject and delete this vendor registration?")) return;
    setActing((a) => ({ ...a, [userId]: "reject" }));
    try {
      await axios.post(`${API}/auth/reject/${userId}`, {}, { headers: authHeaders() });
      load();
    } finally {
      setActing((a) => ({ ...a, [userId]: null }));
    }
  };

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div>
        <h1 className="text-2xl font-bold text-ink tracking-tight">Vendor Approvals</h1>
        <p className="text-sm text-ink/50 mt-0.5">{pending.length} pending registration{pending.length !== 1 ? "s" : ""}</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48 text-ink/40">
          <Loader2 size={20} className="animate-spin mr-2" /> Loading...
        </div>
      ) : pending.length === 0 ? (
        <Card className="bg-white border border-ink/10 shadow-sm rounded-2xl">
          <CardContent className="py-16 flex flex-col items-center justify-center text-center">
            <UserCheck size={32} className="text-ink/30 mb-3" />
            <p className="text-ink/50 font-medium">No pending approvals</p>
            <p className="text-xs text-ink/40 mt-1">All vendor registrations have been reviewed</p>
          </CardContent>
        </Card>
      ) : (
        <div className="bg-white rounded-md border border-ink/10 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-surface">
                <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pending.map((v) => (
                <TableRow key={v.id} data-testid={`pending-vendor-${v.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center text-ink text-xs font-semibold">
                        {v.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <p className="text-sm font-medium text-ink">{v.name}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-ink/60">{v.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs bg-purple-50 text-ink border-warning/40 gap-1">
                      <Clock size={10} /> Pending
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        size="sm"
                        onClick={() => handleApprove(v.id)}
                        disabled={!!acting[v.id]}
                        data-testid={`approve-vendor-${v.id}`}
                        className="h-8 text-xs gap-1.5 bg-success hover:bg-success/90 text-white"
                      >
                        {acting[v.id] === "approve" ? <Loader2 size={12} className="animate-spin" /> : <UserCheck size={12} />}
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReject(v.id)}
                        disabled={!!acting[v.id]}
                        data-testid={`reject-vendor-${v.id}`}
                        className="h-8 text-xs gap-1.5 text-danger border-danger/30 hover:bg-danger/10"
                      >
                        {acting[v.id] === "reject" ? <Loader2 size={12} className="animate-spin" /> : <UserX size={12} />}
                        Reject
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
